import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Users, FileSignature, Receipt, LogOut, Menu, X, ClipboardCheck, Wallet, BarChart3, UserCog, ScrollText, Building, FileCheck2, MessageSquare, MessageSquareText } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/units", label: "Unit", icon: Building2, testid: "nav-units" },
  { to: "/tenants", label: "Penyewa", icon: Users, testid: "nav-tenants" },
  { to: "/contracts", label: "Kontrak", icon: FileSignature, testid: "nav-contracts" },
  { to: "/bills", label: "Tagihan", icon: Receipt, testid: "nav-bills" },
];

const NAV_SECONDARY = [
  { to: "/properties", label: "Properti", icon: Building, testid: "nav-properties" },
  { to: "/transfer-proofs", label: "Bukti Transfer", icon: FileCheck2, testid: "nav-proofs" },
  { to: "/handovers", label: "Serah Terima", icon: ClipboardCheck, testid: "nav-handovers" },
  { to: "/expenses", label: "Biaya", icon: Wallet, testid: "nav-expenses" },
  { to: "/reports", label: "Laporan", icon: BarChart3, testid: "nav-reports" },
  { to: "/wa-logs", label: "Log WhatsApp", icon: MessageSquare, testid: "nav-wa-logs", ownerOnly: true },
  { to: "/wa-templates", label: "Template WA", icon: MessageSquareText, testid: "nav-wa-templates", ownerOnly: true },
  { to: "/users", label: "Pengguna", icon: UserCog, testid: "nav-users", ownerOnly: true },
  { to: "/audit-logs", label: "Audit Log", icon: ScrollText, testid: "nav-audit", ownerOnly: true },
];

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-lg bg-primary grid place-items-center shadow-sm">
        <BrandLogo className="h-6 w-6 text-primary-foreground" />
      </div>
      <div className="leading-none">
        <div className="font-display text-base font-semibold tracking-tight">Kontrakan</div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">SURBAKTI</div>
      </div>
    </div>
  );
}

function SidebarLinks({ onClick, role }) {
  return (
    <nav className="flex flex-col gap-1">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground px-3 pt-1 pb-2">Utama</div>
      {NAV.map(({ to, label, icon: Icon, end, testid }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          data-testid={testid}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground/70 hover:bg-secondary hover:text-foreground"
            )
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground px-3 pt-4 pb-2">Lanjutan</div>
      {NAV_SECONDARY.filter((n) => !n.ownerOnly || role === "owner").map(({ to, label, icon: Icon, testid }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onClick}
          data-testid={testid}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground/70 hover:bg-secondary hover:text-foreground"
            )
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border/60 bg-card/50 backdrop-blur px-4 py-6 gap-6 overflow-y-auto">
        <div className="flex items-center justify-between">
          <BrandMark />
          <NotificationBell />
        </div>
        <SidebarLinks role={user?.role} />
        <div className="mt-auto">
          <div className="rounded-md border border-border/60 p-3 mb-3">
            <div className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Login sebagai</div>
            <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            <div className="mt-1 inline-block rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-secondary-foreground">
              {user?.role}
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Keluar
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 glass-header">
        <div className="flex items-center justify-between px-4 h-14">
          <BrandMark />
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button" aria-label="menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 overflow-y-auto">
              <VisuallyHidden>
                <SheetTitle>Menu Navigasi</SheetTitle>
              </VisuallyHidden>
              <div className="flex flex-col gap-6 min-h-full">
                <div className="flex items-center justify-between">
                  <BrandMark />
                  <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <SidebarLinks role={user?.role} onClick={() => setOpen(false)} />
                <div className="mt-auto">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={handleLogout}
                    data-testid="logout-button-mobile"
                  >
                    <LogOut className="h-4 w-4" /> Keluar
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 md:px-8 lg:px-10 py-6 md:py-8 lg:py-10 pb-24 md:pb-10">
          {children}
        </div>
      </main>

      {/* Mobile bottom nav — hidden on tablet+ (uses hamburger sheet instead) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border/60 bg-background/95 backdrop-blur">
        <div className="grid grid-cols-5">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors duration-200",
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
